// Pure row-selection helpers for the KPI scorecards. Plain JS (no JSX) so the
// node test runner can import it directly.

export const shouldOmit = (v) => v == null || v === '' || v === 'N/A';

export const toNumber = (v) => {
  // Guard null/undefined first: `Number(null)` is 0 and `Number.isFinite(0)` is
  // true, so without this check a missing field would read as 0.
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function rowDimValue(r, field, dimAliases) {
  const keys = dimAliases?.[field];
  const keyList = Array.isArray(keys) && keys.length > 0 ? keys : [field];
  for (const k of keyList) {
    const v = r?.[k];
    if (v == null) continue;
    return v;
  }
  return null;
}

export function dimEq(a, b) {
  if (a == null || b == null) return a === b;
  if (typeof a === 'string' && typeof b === 'string') {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }
  return a === b;
}

/**
 * All scorecard rows that match chart dims (multi-select + strict fields).
 * Wildcard dims are not enforced on the row (same as matchSingleRow pool before aggregate pick).
 */
export function rowsMatchingDims(rows, dims, dimAliases) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (!dims) return rows.slice();

  const normalized = {};
  for (const [k, v] of Object.entries(dims)) {
    const single = Array.isArray(v) ? (v.length === 1 ? v[0] : null) : v;
    normalized[k] = single;
  }

  let pool = rows;

  for (const [k, v] of Object.entries(dims)) {
    if (!Array.isArray(v) || v.length <= 1) continue;
    const picks = v.filter((x) => !shouldOmit(x));
    if (picks.length === 0) continue;
    pool = pool.filter((r) => {
      const rv = rowDimValue(r, k, dimAliases);
      if (rv == null) return true;
      return picks.some((want) => dimEq(rv, want));
    });
  }
  if (pool.length === 0) return [];

  const strictPass = (r) => {
    for (const [field, want] of Object.entries(normalized)) {
      if (shouldOmit(want)) continue;
      const rowVal = rowDimValue(r, field, dimAliases);
      if (rowVal == null) continue;
      if (!dimEq(rowVal, want)) return false;
    }
    return true;
  };

  return pool.filter(strictPass);
}

/**
 * Pick a single row from a metric's `rows` array using user-selected dimensions.
 * Single-select / "All" path. Wildcard dims prefer the backend aggregate row
 * (dim = null). Returns null when nothing matches.
 */
export function matchSingleRow(rows, dims, dimAliases) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (!dims) return rows[0] || null;

  const wildcardFields = [];
  for (const [k, v] of Object.entries(dims)) {
    const single = Array.isArray(v) ? (v.length === 1 ? v[0] : null) : v;
    if (shouldOmit(single)) wildcardFields.push(k);
  }

  const pool = rowsMatchingDims(rows, dims, dimAliases);
  if (pool.length === 0) return null;
  if (wildcardFields.length === 0) return pool[0] || null;

  const score = (r) => wildcardFields.reduce(
    (n, f) => n + (rowDimValue(r, f, dimAliases) == null ? 1 : 0), 0
  );

  let best = pool[0];
  let bestScore = score(best);
  for (let i = 1; i < pool.length; i++) {
    const s = score(pool[i]);
    if (s > bestScore) { best = pool[i]; bestScore = s; }
  }
  return best || null;
}

// ---------- row classifiers ----------

const GH_RE = /^(gh|greenhouse|green house|hothouse)$/i;

const rowIsConventional = (r, dimAliases) => {
  const t = rowDimValue(r, 'type', dimAliases);
  return t == null || dimEq(t, 'Conventional');
};

const rowIsGreenhouse = (r, dimAliases) => {
  const e = rowDimValue(r, 'environment', dimAliases);
  return typeof e === 'string' && GH_RE.test(e.trim());
};

// ---------- default-to-one selection ----------

/** Only these dims can become "the one" a scorecard shows. */
export const SELECTION_KEYS = ['origin', 'destination', 'variety'];

const SIZE_KEYS = ['size', 'sizeGroup'];

// Same vocabulary as SENTINEL_OPTION_VALUES in analyticsHelpers (kept local so
// this file stays free of app imports).
const SENTINELS = new Set(['n/a', 'na', 'not classified', 'none', 'null', '']);

/** True for a placeholder spelling (N/A, Not Classified, None, ...), any casing. */
export const isSentinelValue = (v) =>
  v != null && SENTINELS.has(String(v).trim().toLowerCase());

const isConcrete = (v) => v != null && !isSentinelValue(v);

const picksOf = (v) => {
  if (Array.isArray(v)) return v.filter((x) => !shouldOmit(x));
  return shouldOmit(v) ? [] : [v];
};

const normKey = (v) => String(v).trim().toLowerCase();

/**
 * Chart-side helper: a dim the user left on "All" must reach the scorecards as
 * [] (not as the full option list). Narrowed selections are returned as is.
 * A list covering every option (a singleton option list included) is All, as
 * in the chart. Scalars are untouched, so a scalar single selection stays strict.
 */
export function kpiDim(selected, options) {
  if (!Array.isArray(selected)) return selected;
  if (selected.length === 0) return [];
  if (!Array.isArray(options) || options.length < 1) return selected;
  const have = new Set(selected.map(normKey));
  return options.every((o) => have.has(normKey(o))) ? [] : selected;
}

const hasDim = (dims, k) =>
  !!dims && Object.prototype.hasOwnProperty.call(dims, k);

/** Supported selection dims the user has not narrowed to exactly one value. */
export function activeSelectionKeys(dims, supportedKeys = SELECTION_KEYS) {
  if (!dims) return [];
  return SELECTION_KEYS.filter(
    (k) => supportedKeys.includes(k) && hasDim(dims, k) && picksOf(dims[k]).length !== 1
  );
}

/** dims with every narrowed size / sizeGroup cleared, or null when none is narrowed. */
export function relaxSizeDims(dims) {
  if (!dims) return null;
  const narrowed = SIZE_KEYS.filter((k) => hasDim(dims, k) && picksOf(dims[k]).length > 0);
  if (narrowed.length === 0) return null;
  const copy = { ...dims };
  narrowed.forEach((k) => { copy[k] = []; });
  return copy;
}

/**
 * matchSingleRow, then (only when it finds nothing and a size filter is
 * narrowed) once more without the size filter: KPI rows use item_size while the
 * chart may filter on a different size vocabulary.
 */
export function matchWithFallback(rows, dims, dimAliases) {
  const strict = matchSingleRow(rows, dims, dimAliases);
  if (strict) return { row: strict, relaxed: false };
  const relaxedDims = relaxSizeDims(dims);
  if (!relaxedDims) return { row: null, relaxed: false };
  const row = matchSingleRow(rows, relaxedDims, dimAliases);
  return { row, relaxed: !!row };
}

const WORDS = { origin: 'origins', destination: 'terminals', variety: 'varieties' };
export const RELAXED_NOTE = 'size filter not applied: no row for the selected size';
export const DIFFERS_NOTE = 'differs from the other cards: no data for their selection';

// Order-independent serialisation of a row, the last tiebreak so that
// reversing the input never changes which row wins.
const stableKey = (r) => {
  if (r == null || typeof r !== 'object') return String(r);
  return JSON.stringify(Object.keys(r).sort().map((k) => [k, r[k]]));
};

const tupleOf = (entry, keys) => {
  const t = {};
  keys.forEach((k, i) => { t[k] = entry.vals[i]; });
  return t;
};

const tupleMatches = (entry, keys, tuple) =>
  keys.every((k, i) => !(k in tuple) || dimEq(entry.vals[i], tuple[k]));

function rankTop(list, primary, hasPct) {
  let top = null;
  for (const e of list) {
    if (top == null) { top = e; continue; }
    const dp = primary(e) - primary(top);
    if (dp !== 0) { if (dp > 0) top = e; continue; }
    if (hasPct && e.pct != null && top.pct != null && e.pct !== top.pct) {
      if (e.pct > top.pct) top = e;
      continue;
    }
    const ev = e.value ?? -Infinity;
    const tv = top.value ?? -Infinity;
    if (ev !== tv) { if (ev > tv) top = e; continue; }
    if (e.conv !== top.conv) { if (e.conv) top = e; continue; }
    // Breadth is only a late tie-break: broader roll-up, then more fixed context.
    if ((e.wild ?? 0) !== (top.wild ?? 0)) { if (e.wild > top.wild) top = e; continue; }
    if ((e.fixed ?? 0) !== (top.fixed ?? 0)) { if (e.fixed > top.fixed) top = e; continue; }
    const cmp = e.label.localeCompare(top.label);
    if (cmp !== 0) { if (cmp < 0) top = e; continue; }
    const sk = e.skey.localeCompare(top.skey);
    if (sk < 0) top = e;
  }
  return top;
}

const BASIS_WORDS = {
  pct: 'highest % change',
  conventional: 'highest Conventional price',
  gh: 'highest GH price',
  value: 'highest price',
};

function pickFromPool(pool, dims, keys, o) {
  const { dimAliases, pctKey, valueKey, preferTuple } = o;
  const wildFields = Object.keys(dims || {}).filter(
    (f) => !keys.includes(f) && picksOf(dims[f]).length !== 1
  );
  const fixedFields = Object.keys(dims || {}).filter((f) => picksOf(dims[f]).length === 1);

  const entries = pool.map((r, index) => {
    const vals = keys.map((k) => rowDimValue(r, k, dimAliases));
    return {
      r,
      index,
      vals,
      label: vals.filter((v) => v != null).join(' · '),
      pct: pctKey ? toNumber(r[pctKey]) : null,
      value: toNumber(r[valueKey]),
      conv: rowIsConventional(r, dimAliases),
      skey: stableKey(r),
      // Only a null context value marks a backend roll-up; a placeholder
      // spelling (N/A, Not Classified) is not proof of one.
      wild: wildFields.reduce((n, f) => n + (rowDimValue(r, f, dimAliases) == null ? 1 : 0), 0),
      fixed: fixedFields.reduce((n, f) => n + (rowDimValue(r, f, dimAliases) == null ? 0 : 1), 0),
    };
  }).filter((e) => e.pct != null || e.value != null);

  const build = (row, label, basis, extra = {}) => ({
    row, label, basis, tuple: null, count: 0, dimWord: WORDS[keys[0]] || 'items', differs: false, ...extra,
  });

  // Stages, best first. Every eligible row is considered for the stage before
  // any row is set aside, so neither input order nor a sibling row without the
  // stage's number can hide the winner.
  const STAGES = [
    { basis: 'pct', ok: (e) => e.pct != null, primary: (e) => e.pct },
    { basis: 'conventional', ok: (e) => e.conv && e.value != null, primary: (e) => e.value },
    { basis: 'gh', ok: (e) => rowIsGreenhouse(e.r, dimAliases) && e.value != null, primary: (e) => e.value },
    { basis: 'value', ok: (e) => e.value != null, primary: (e) => e.value },
  ];

  const concreteEntries = entries.filter((e) => e.vals.every(isConcrete));
  if (concreteEntries.length) {
    let candidates = concreteEntries;
    let differs = false;
    if (preferTuple) {
      const narrowed = concreteEntries.filter((e) => tupleMatches(e, keys, preferTuple));
      if (narrowed.length) candidates = narrowed; else differs = true;
    }
    for (const stage of STAGES) {
      const eligible = candidates.filter(stage.ok);
      if (!eligible.length) continue;
      const top = rankTop(eligible, stage.primary, !!pctKey);
      return build(top.r, top.label, stage.basis, {
        tuple: tupleOf(top, keys),
        count: new Set(eligible.map((e) => normKey(e.vals[0]))).size,
        differs,
      });
    }
  }

  // No concrete row: backend aggregates (selection dims null), broadest first.
  const aggs = entries.filter((e) => e.vals.some((v) => v == null) && e.vals.every((v) => v == null || isConcrete(v)));
  if (aggs.length) {
    const nulls = (e) => e.vals.filter((v) => v == null).length;
    const top = aggs.reduce((a, b) => {
      if (nulls(b) !== nulls(a)) return nulls(b) > nulls(a) ? b : a;
      return b.skey.localeCompare(a.skey) < 0 ? b : a;
    });
    const all = nulls(top) === keys.length;
    return build(top.r, all ? 'All selected' : top.label, all ? 'aggregate' : 'partial');
  }
  // Last resort: rows whose selection value is a placeholder; shown unlabelled.
  const unlabeled = entries.filter((e) => e.vals.every((v) => v != null));
  if (unlabeled.length) {
    const top = rankTop(unlabeled, (e) => (e.pct != null ? 1 : 0), !!pctKey);
    return build(top.r, '', 'unlabeled');
  }
  return null;
}

/**
 * Default-to-one: when origin / destination / variety is "All" ([]) or holds
 * several picks, choose one concrete row by the rule highest signed % -> highest
 * Conventional price -> GH -> any price, and say which one. Selection dims the
 * metric's rows do not carry (`supportedKeys`) are never used or labelled. With
 * no active selection dim this is exactly matchSingleRow (plus the size
 * fallback), so single-select behaviour is unchanged.
 *
 * opts: { dimAliases, pctKey, valueKey, supportedKeys, preferTuple,
 *         scopeLabel, scopeTitle }
 * @returns {{row, label, title, basis, tuple, count, dimWord, defaulted,
 *   relaxed, differs}}
 */
export function pickDefaultOne(rows, dims, opts = {}) {
  const { dimAliases, valueKey = 'value', supportedKeys = SELECTION_KEYS, scopeLabel, scopeTitle } = opts;
  const empty = {
    row: null, label: null, title: null, basis: 'none', tuple: null,
    count: 0, dimWord: '', defaulted: false, relaxed: false, differs: false,
  };
  if (!Array.isArray(rows) || rows.length === 0) return empty;
  const keys = activeSelectionKeys(dims, supportedKeys);

  if (keys.length === 0) {
    const m = matchWithFallback(rows, dims, dimAliases);
    if (!m.row) return empty;
    // A metric with a fixed scope (terminal week-over-week averages every
    // terminal and variety) is always labelled with it, whatever is selected.
    const scoped = !!scopeLabel;
    const title = [scoped ? scopeTitle || scopeLabel : null, m.relaxed ? RELAXED_NOTE : null]
      .filter(Boolean).join(' (').concat(scoped && m.relaxed ? ')' : '');
    return { ...empty, row: m.row, relaxed: m.relaxed, label: scoped ? scopeLabel : null, title: title || null };
  }

  const o = { ...opts, valueKey };
  let relaxed = false;
  let res = pickFromPool(rowsMatchingDims(rows, dims, dimAliases), dims, keys, o);
  if (!res) {
    const relaxedDims = relaxSizeDims(dims);
    if (relaxedDims) {
      res = pickFromPool(rowsMatchingDims(rows, relaxedDims, dimAliases), relaxedDims, keys, o);
      relaxed = !!res;
    }
  }
  if (!res) return empty;

  const word = res.dimWord;
  let title;
  if (res.basis === 'aggregate') title = `Showing all selected ${word}`;
  else if (res.basis === 'partial') title = `Showing ${res.label} (partial aggregate)`;
  else if (res.basis === 'unlabeled') title = 'Showing the only available row';
  else if (res.count > 1) title = `Showing ${res.label}: ${BASIS_WORDS[res.basis]} of ${res.count} ${word}`;
  else title = `Showing ${res.label}`;
  const notes = [relaxed ? RELAXED_NOTE : null, res.differs ? DIFFERS_NOTE : null].filter(Boolean);
  if (notes.length) title += ` (${notes.join('; ')})`;

  return {
    row: res.row,
    label: res.label || null,
    title,
    basis: res.basis,
    tuple: res.tuple,
    count: res.count,
    dimWord: word,
    defaulted: true,
    relaxed,
    differs: res.differs,
  };
}

/**
 * The one selection tuple shared by a row of tiles: taken from the first pick
 * that ranked on a % (specs are passed in priority order), else the first pick
 * that has any tuple.
 */
export function chooseSharedTuple(picks) {
  const withRow = (picks || []).filter((p) => p && p.row && p.tuple);
  const byPct = withRow.find((p) => p.basis === 'pct');
  return (byPct || withRow[0] || {}).tuple || null;
}

const hasRangeEnds = (list) =>
  list.some((r) => toNumber(r?.high_value) != null) && list.some((r) => toNumber(r?.low_value) != null);

function rangeRowsOnce(rows, dims, dimAliases, supportedKeys, tuple) {
  const base = { applied: false, tuple: null, differs: false };
  const pool = rowsMatchingDims(rows, dims, dimAliases);
  const keys = activeSelectionKeys(dims, supportedKeys);
  if (keys.length === 0) return { ...base, rows: pool };
  const anyMulti = keys.some((k) => picksOf(dims[k]).length > 1);
  const concrete = pool.filter((r) => keys.every((k) => isConcrete(rowDimValue(r, k, dimAliases))));
  if (concrete.length === 0) return { ...base, rows: anyMulti ? [] : pool };

  if (tuple) {
    const hit = concrete.filter((r) =>
      keys.every((k) => !(k in tuple) || dimEq(rowDimValue(r, k, dimAliases), tuple[k])));
    if (hit.length && hasRangeEnds(hit)) return { ...base, rows: hit, applied: true, tuple };
  }

  // No shared tuple, or it has no usable high AND low: take ONE other tuple that does
  // (the one with the highest high value) instead of mixing every variety.
  const groups = new Map();
  for (const r of concrete) {
    const vals = keys.map((k) => rowDimValue(r, k, dimAliases));
    const key = vals.map(normKey).join('\u0001');
    if (!groups.has(key)) groups.set(key, { vals, rows: [] });
    groups.get(key).rows.push(r);
  }
  const usable = [...groups.values()].filter((g) => hasRangeEnds(g.rows));
  // Never mix combinations: no single one has both ends -> no range at all.
  // Rows with no range data at all (generic callers) keep the legacy pool.
  if (usable.length === 0) {
    const hasEnds = concrete.some((r) => toNumber(r?.high_value) != null || toNumber(r?.low_value) != null);
    return { ...base, rows: hasEnds || tuple ? [] : anyMulti ? concrete : pool };
  }
  const topHigh = (g) => Math.max(...g.rows.map((r) => toNumber(r.high_value) ?? -Infinity));
  const label = (g) => g.vals.join(' \u00b7 ');
  const best = usable.reduce((a, b) => {
    if (topHigh(b) !== topHigh(a)) return topHigh(b) > topHigh(a) ? b : a;
    return label(b).localeCompare(label(a)) < 0 ? b : a;
  });
  const used = {};
  keys.forEach((k, i) => { used[k] = best.vals[i]; });
  return { ...base, rows: best.rows, tuple: used, differs: !!tuple };
}

/**
 * Rows feeding the range-spread tile. With an active selection dim only
 * concrete rows count (backend roll-ups and placeholder-valued rows are
 * dropped), narrowed to the shared tuple when it has a usable high and low;
 * otherwise to one other usable tuple (`differs`). Like the % tiles, a strict
 * match is tried first and the size filter is dropped only when it yields no
 * usable range (`relaxed`).
 * @returns {{rows: object[], applied: boolean, tuple: object|null,
 *   differs: boolean, relaxed: boolean}}
 */
export function rangeRows(rows, dims, dimAliases, { supportedKeys, tuple } = {}) {
  const strict = rangeRowsOnce(rows, dims, dimAliases, supportedKeys, tuple);
  if (hasRangeEnds(strict.rows)) return { ...strict, relaxed: false };
  const relaxedDims = relaxSizeDims(dims);
  if (relaxedDims) {
    const loose = rangeRowsOnce(rows, relaxedDims, dimAliases, supportedKeys, tuple);
    if (hasRangeEnds(loose.rows)) return { ...loose, relaxed: true };
  }
  return { ...strict, relaxed: false };
}

/**
 * Matching rows with backend aggregate (null selection dim) rows removed.
 * Returns an empty list when only aggregates exist for a multi-select.
 */
export function concreteRowsMatchingDims(rows, dims, dimAliases) {
  return rangeRows(rows, dims, dimAliases).rows;
}
